import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ReportsList } from '@/components/reports/ReportsList';
import { ReportLoading } from '@/components/reports/ReportLoading';
import { NoReportsFound } from '@/components/reports/NoReportsFound';
import { ReportsPagination } from './ReportsPagination';
import { formatDate, formatTime } from '@/utils/reportFormatters';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tag } from "lucide-react";
import { Label } from "@/components/ui/label";

interface AdminContentProps {
  isLoading: boolean;
  reports: any[];
  currentPage: number;
  totalPages: number;
  onReportUpdate: () => void;
  onPageChange: (page: number) => void;
  search: string;
  setSearch: (q: string) => void;
  onSearch: () => void;
  showDeductedOnly: boolean;
  setShowDeductedOnly: (b: boolean) => void;
  showOtherSubjectsOnly: boolean;
  setShowOtherSubjectsOnly: (b: boolean) => void;
}

export const AdminContent: React.FC<AdminContentProps> = ({
  isLoading,
  reports,
  currentPage,
  totalPages,
  onReportUpdate,
  onPageChange,
  search,
  setSearch,
  onSearch,
  showDeductedOnly,
  setShowDeductedOnly,
  showOtherSubjectsOnly,
  setShowOtherSubjectsOnly
}) => {
  // กด Enter เพื่อ search ได้ด้วย
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  return (
    <Card className="shadow-lg">
      <CardContent className="p-6">
        {/* input ค้นหา */}
        <div className="mb-4 flex flex-col md:flex-row gap-2 md:items-center justify-between">
          <div className="flex w-full max-w-md items-center gap-2">
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="ค้นหาด้วยชื่อนักเรียน / ครูผู้สอน / รหัสครู / วิชา / อีเมล"
              onKeyDown={handleInputKeyDown}
              className="flex-1"
            />
            <Button type="button" onClick={onSearch}>ค้นหา</Button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-2 md:mt-0">
            <div className="flex items-center space-x-2 px-3 py-2 border rounded-md bg-white">
              <Checkbox
                id="showDeductedOnly"
                checked={showDeductedOnly}
                onCheckedChange={v => setShowDeductedOnly(!!v)}
              />
              <label htmlFor="showDeductedOnly" className="text-sm select-none">
                แสดงเฉพาะรายการที่ไม่ได้หักชั่วโมง
              </label>
            </div>
            
            {/* Other subject checkbox */}
            <div className="flex items-center space-x-2 px-3 py-2 border rounded-md bg-white">
              <Checkbox 
                id="showOtherSubjectsOnly" 
                checked={showOtherSubjectsOnly}
                onCheckedChange={(checked) => setShowOtherSubjectsOnly(checked as boolean)} 
              />
              <Label 
                htmlFor="showOtherSubjectsOnly" 
                className="text-sm cursor-pointer flex items-center gap-1"
              >
                <Tag className="h-3 w-3 text-amber-600" />
                <span>แสดงเฉพาะหัวข้ออื่นๆ</span>
              </Label>
            </div>
          </div>
        </div>
        {isLoading ? (
          <ReportLoading />
        ) : reports.length > 0 ? (
          <>
            <ReportsList
              reports={reports}
              formatDate={formatDate}
              formatTime={formatTime}
              showTeacherDisplay={true}
              showAdminColumns={true}
              onReportUpdate={onReportUpdate}
            />
            <ReportsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          </>
        ) : (
          <NoReportsFound />
        )}
      </CardContent>
    </Card>
  );
};
